import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  ChevronRight,
  Monitor,
  LayoutGrid,
  MailPlus,
  Server,
  Users,
  ShieldCheck,
  Database,
  Cloud,
  Network,
  Lightbulb,
  Phone,
} from "lucide-react";
import { services, companyInfo } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = {
  Monitor, LayoutGrid, MailPlus, Server, Users, ShieldCheck,
  DatabaseBackup: Database, Cloud, Network, Lightbulb,
};

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function Services() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-3xl">
            <Badge
              variant="secondary"
              className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
            >
              Our Services
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              IT Services & Support for Australian Small Businesses
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              From proactive managed support and cybersecurity to cloud migration
              and strategic consulting — we deliver the full spectrum of IT
              services your business needs. Every service explained in plain
              English, delivered by local Australian experts.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Monitor;
              return (
                <Link key={service.id} to={`/services/${service.id}`}>
                  <Card className="service-card h-full border-slate-100 hover:border-blue-100 bg-white group cursor-pointer">
                    <CardContent className="p-7">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <h2 className="text-lg font-semibold text-slate-900 mb-2.5">
                        {service.title}
                      </h2>
                      <p className="text-sm text-slate-500 leading-relaxed mb-4">
                        {service.shortDesc}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 group-hover:gap-2.5 transition-all duration-200">
                        Learn more
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4">
              Not Sure What You Need?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Every business is different. Contact us for a free consultation and
              we'll recommend the right services for your needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Get a Free Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-300 text-slate-700"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
